import {
    AssignedNodeRef,
    ContentTypeGroupStatus,
    CtgStatus,
    LinkedBy,
    NodeContentO,
    ObjectSchema,
} from '@src/__generated__/types';
import { useLanguageCtx } from '@src/_new/modules/language';
import { NodeReadonlyState } from '@src/_new/modules/node-readonly';
import { useCgtEditNodeHelpers } from '@src/hooks/cgt/useCgtHelpers';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { Omit } from 'lodash';
import { useContext } from 'react';

import { GetNodeQuery } from '../Hierarchy.generated';
import { CombinedInfo, IProcessNodeData, processNodeData } from '../shared/EditNodeHelpers';

type MappedContents = NodeContentO & {
    inherited?: boolean;
};

export type NodeData = null | {
    id: string;
    _rev?: string | null;
    hierarchyId: string;
    hierarchyCode: string;
    code: string;
    parent: string;
    disabled: boolean;
    contents: MappedContents[];
    contentTypeGroupStatus: ContentTypeGroupStatus[] | undefined | null;
    productInfo: Omit<ObjectSchema, 'isReferenced'>;
    combinedInfo: CombinedInfo;
    disableDeleteHierarchy: boolean;
    disableDelete: boolean;
    disableEditCode: boolean;
    linkedBy: (LinkedBy | null)[] | undefined | null;
    listName: string | null | undefined;
    nodePublished: CtgStatus;
    createdAt: number;
    updatedAt: number;
    assignedOwner: AssignedNodeRef | null | undefined;
    assignedDealership: AssignedNodeRef | null | undefined;
    assignedClient: AssignedNodeRef | null | undefined;
    readonlyState: NodeReadonlyState | null;
};

const useTransformEditNodeData = (createNode = false) => {
    const { cgtSettings } = useContext(SettingsContext);
    const { sortedIsoCodes } = useLanguageCtx();

    const { initCGTDefaultValue, shouldInitCGTValue, filterCGTOptions } =
        useCgtEditNodeHelpers(createNode);

    const transformEditNodeData = (data: GetNodeQuery): NodeData => {
        const [hierarchy] = data?.hierarchies ?? [];
        const [node] = hierarchy?.nodes || [];

        if (!hierarchy || !node || !hierarchy.objectSchema || !node.contents) {
            return null;
        }

        const shouldInit = shouldInitCGTValue(hierarchy.id);

        // map contents with inherited and values
        const contents = node?.contents?.map(ct => {
            const value = shouldInit ? initCGTDefaultValue(ct.contentType) : null;
            const inherited = !createNode ? ct?.inherited : true;
            if (value) {
                return {
                    ...ct,
                    inherited: false,
                    value: createNode || shouldInit ? value : ct?.value,
                };
            }
            return {
                ...ct,
                inherited,
                value: ct.value
                    ?.filter(v => sortedIsoCodes.has(v.languageIsoCode))
                    .sort(
                        (a, b) =>
                            (sortedIsoCodes.get(a.languageIsoCode) ?? 0) -
                            (sortedIsoCodes.get(b.languageIsoCode) ?? 0),
                    ),
            };
        });

        // map combinedInfo, using previously mapped contents and adding a default value
        // (if needed) to contents that don't have any
        const combinedInfo = processNodeData({
            objectSchema: hierarchy.objectSchema as IProcessNodeData['objectSchema'],
            contents: contents as IProcessNodeData['contents'],
        }).map(comb => {
            const ct = contents.find(c => c.contentType === comb.contentType);
            if ((!ct || ct.type === 'link') && (createNode || shouldInit)) {
                const value = shouldInit ? initCGTDefaultValue(comb.contentType) : null;
                if (value) {
                    return {
                        ...comb,
                        inherited: false,
                        value,
                    };
                }
            }
            return comb;
        });

        const productInfo: typeof hierarchy.objectSchema = {
            ...hierarchy.objectSchema,
            contentTypeGroups: hierarchy.objectSchema.contentTypeGroups?.map(group => ({
                ...group,
                contentTypes: group.contentTypes?.map(content => {
                    if (content.code !== cgtSettings?.fields?.typeField.contentTypeCode)
                        return content;

                    return {
                        ...content,
                        optionalValues: filterCGTOptions(
                            data,
                            content,
                            combinedInfo.find(c => c.contentType === content.code) as any,
                        ),
                    };
                }),
            })),
        };

        return {
            id: node.id,
            _rev: node._rev,
            hierarchyId: hierarchy.id,
            hierarchyCode: hierarchy.code,
            code: node.code,
            parent: node.parent, // This type requires modification since `parent` is always defined. PIM-297 has the fix but it hasn't been added yet.
            disabled: !!node.disabled,
            contents,
            contentTypeGroupStatus: node.contentTypeGroupStatus,
            productInfo,
            combinedInfo,
            disableDeleteHierarchy: hierarchy.objectSchema.disableDeleteHierarchy ?? false,
            disableDelete: hierarchy.objectSchema.disableDelete ?? false,
            disableEditCode: hierarchy.objectSchema.disableEditCode ?? false,
            linkedBy: node.linkedBy ?? [],
            listName: node.listName,
            nodePublished: node.nodePublished,
            createdAt: node.createdAt,
            updatedAt: node.updatedAt,
            assignedOwner: node.assignedOwner,
            assignedDealership: node.assignedDealership,
            assignedClient: node.assignedClient,
            readonlyState: node.readonlyState ?? null,
        };
    };

    return transformEditNodeData;
};

export default useTransformEditNodeData;
